/**
 * useReasoningStream — SSE hook for real-time LLM reasoning display.
 *
 * Parses the standard SSE reasoning protocol events:
 *   event: reasoning   { text, seq, source?, kind? }
 *   event: tool_use    { tool, status, args?, result? }
 *   event: done        { reply, artifacts, reasoning_trace, usage }
 *   event: error       { detail, code? }
 *   event: status      { step }
 *
 * The `kind` field on reasoning events distinguishes:
 *   kind="step"  (default / omitted) — a complete semantic thought; appended
 *                as a numbered entry in reasoningSteps.
 *   kind="delta" — a raw LLM token chunk; accumulated into liveText and
 *                  also kept in rawTokens[] so the UI can show the verbatim
 *                  stream under an expandable "Raw tokens" section.
 *
 * Returns:
 *   reasoningSteps  {Array}    [{seq, text, source, timestamp}]
 *   liveText        {string}   Current reasoning sentence accumulating
 *   rawTokens       {Array}    Individual delta token strings (verbatim)
 *   thinkingText    {string}   Concatenation of all delta tokens this turn
 *   toolActions     {Array}    [{tool, status, args, result, timestamp}]
 *   isStreaming      {boolean}
 *   isDone          {boolean}
 *   donePayload     {Object}
 *   error           {string}
 *   reset           {fn}
 *   processSSELine  {fn}
 *   parseSSEChunk   {fn}
 */
import { useState, useCallback, useRef } from 'react';

const DELTA_FLUSH_CHARS = 300;

export const useReasoningStream = () => {
    const [reasoningSteps, setReasoningSteps] = useState([]);
    const [liveText, setLiveText] = useState('');
    const [rawTokens, setRawTokens] = useState([]);
    const [thinkingText, setThinkingText] = useState('');
    const [toolActions, setToolActions] = useState([]);
    const [isStreaming, setIsStreaming] = useState(false);
    const [isDone, setIsDone] = useState(false);
    const [donePayload, setDonePayload] = useState(null);
    const [error, setError] = useState('');

    const liveTextRef = useRef('');
    const thinkingTextRef = useRef('');
    const rawTokensRef = useRef([]);
    const reasoningStepsRef = useRef([]);
    const toolActionsRef = useRef([]);
    const seqRef = useRef(0);
    const lastStepTextRef = useRef('');

    const reset = useCallback(() => {
        setReasoningSteps([]);
        setLiveText('');
        setRawTokens([]);
        setThinkingText('');
        setToolActions([]);
        setIsStreaming(false);
        setIsDone(false);
        setDonePayload(null);
        setError('');
        liveTextRef.current = '';
        thinkingTextRef.current = '';
        rawTokensRef.current = [];
        reasoningStepsRef.current = [];
        toolActionsRef.current = [];
        seqRef.current = 0;
        lastStepTextRef.current = '';
    }, []);

    /** Sync snapshot for message history (React state may lag SSE done). */
    const getTurnSnapshot = useCallback(() => ({
        reasoningSteps: [...reasoningStepsRef.current],
        rawTokens: [...rawTokensRef.current],
        thinkingText: thinkingTextRef.current,
        toolActions: [...toolActionsRef.current],
    }), []);

    const _flushLiveText = useCallback((source = 'llm') => {
        const buffered = liveTextRef.current.trim();
        if (!buffered) return;
        seqRef.current += 1;
        const flushedStep = {
            seq: seqRef.current,
            text: buffered,
            source,
            timestamp: Date.now(),
        };
        reasoningStepsRef.current = [...reasoningStepsRef.current, flushedStep];
        setReasoningSteps(reasoningStepsRef.current);
        liveTextRef.current = '';
        setLiveText('');
    }, []);

    const handleReasoningEvent = useCallback((data) => {
        setIsStreaming(true);
        const { text, seq, source, kind } = data;
        const stepText = text || '';

        if (kind === 'delta') {
            // Keep every raw token for the expandable raw-token inspector
            rawTokensRef.current = [...rawTokensRef.current, stepText];
            setRawTokens(rawTokensRef.current);
            thinkingTextRef.current += stepText;
            setThinkingText(thinkingTextRef.current);

            const next = liveTextRef.current + stepText;
            liveTextRef.current = next;
            setLiveText(next);

            if (next.includes('\n') || next.length >= DELTA_FLUSH_CHARS) {
                _flushLiveText(source || 'llm');
            }
            return;
        }

        _flushLiveText(source || 'llm');

        const incomingSeq = seq != null ? seq : null;
        if (incomingSeq != null && incomingSeq <= seqRef.current) return;
        if (incomingSeq == null && stepText === lastStepTextRef.current) return;

        lastStepTextRef.current = stepText;
        seqRef.current = incomingSeq != null ? incomingSeq : seqRef.current + 1;

        const step = {
            seq: seqRef.current,
            text: stepText,
            source: source || 'status',
            timestamp: Date.now(),
        };
        reasoningStepsRef.current = [...reasoningStepsRef.current, step];
        setReasoningSteps(reasoningStepsRef.current);
    }, [_flushLiveText]);

    const handleToolUseEvent = useCallback((data) => {
        const { tool, status, args, result } = data;
        const action = {
            tool,
            status,
            args: args || null,
            result: result || null,
            timestamp: Date.now(),
        };

        const prev = toolActionsRef.current;
        let next;
        if (status === 'started') {
            next = [...prev, action];
        } else {
            const idx = prev.findIndex(
                (a) => a.tool === tool && a.status === 'started',
            );
            if (idx >= 0) {
                next = [...prev];
                next[idx] = { ...next[idx], ...action };
            } else {
                next = [...prev, action];
            }
        }
        toolActionsRef.current = next;
        setToolActions(next);
    }, []);

    const handleDoneEvent = useCallback((data) => {
        _flushLiveText('llm');
        setIsStreaming(false);
        setIsDone(true);
        setDonePayload(data);
        setLiveText('');
        liveTextRef.current = '';
    }, [_flushLiveText]);

    const handleErrorEvent = useCallback((data) => {
        setIsStreaming(false);
        setError(data.detail || 'Unknown error');
    }, []);

    const processSSELine = useCallback((eventType, dataStr) => {
        let data;
        try {
            data = typeof dataStr === 'string' ? JSON.parse(dataStr) : dataStr;
        } catch {
            return;
        }

        switch (eventType) {
            case 'reasoning':
                handleReasoningEvent(data);
                break;
            case 'tool_use':
                _flushLiveText('llm');
                handleToolUseEvent(data);
                break;
            case 'done':
                handleDoneEvent(data);
                break;
            case 'error':
                handleErrorEvent(data);
                break;
            case 'status':
            case 'thinking':
                if (data.step || data.text) {
                    handleReasoningEvent({
                        text: data.step || data.text,
                        source: 'status',
                        kind: 'step',
                    });
                }
                break;
            default:
                break;
        }
    }, [handleReasoningEvent, handleToolUseEvent, handleDoneEvent, handleErrorEvent, _flushLiveText]);

    const parseSSEChunk = useCallback((chunk) => {
        const lines = chunk.split('\n');
        let currentEvent = '';

        for (const line of lines) {
            if (line.startsWith('event: ')) {
                currentEvent = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
                const dataStr = line.slice(6);
                if (currentEvent) {
                    processSSELine(currentEvent, dataStr);
                    currentEvent = '';
                }
            }
        }
    }, [processSSELine]);

    return {
        reasoningSteps,
        liveText,
        rawTokens,
        thinkingText,
        toolActions,
        isStreaming,
        isDone,
        donePayload,
        error,
        reset,
        getTurnSnapshot,
        processSSELine,
        parseSSEChunk,
    };
};

export default useReasoningStream;
